// AdapterSheet.jsx — Side drawer for editing an adapter.
//
// Built-ins: name/base_url/request_format read-only; api_key/models/active editable.
// Custom: all fields editable + Delete button visible.
// Esc closes. Submit/save via Cmd+S handled at form level.

import { useState, useEffect } from 'react';
import {
  X,
  Save,
  Trash2,
  Zap,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { getPreset } from '../../../lib/adapterPresets.js';
import { adapterConnected } from '../../../hooks/useUnifiedModels.js';
import ModelStatusDot from '../../shared/ModelStatusDot.jsx';
import ModelMultiSelect from './ModelMultiSelect.jsx';
import AdapterLogo from './AdapterLogo.jsx';

export default function AdapterSheet({ adapter, onClose, onSave, onDelete, onTest, onListModels }) {
  const [displayName, setDisplayName] = useState(adapter.display_name);
  const [description, setDescription] = useState(adapter.description || '');
  const [baseUrl, setBaseUrl] = useState(adapter.base_url);
  const [defaultModel, setDefaultModel] = useState(adapter.default_model || '');
  const [availableModels, setAvailableModels] = useState(
    adapter.available_models || [],
  );
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [isActive, setIsActive] = useState(!!adapter.is_active);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestMsg, setSuggestMsg] = useState(null);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isBuiltin = !!adapter.is_builtin;
  const preset = getPreset(adapter.name);

  // Status da bolinha: reflete o estado AO VIVO do form (toggle Ativo + key
  // digitada agora) por cima do estado persistido. Cloud → tem key; local →
  // ativo. Fonte única: adapterConnected do useUnifiedModels.
  const connected = adapterConnected({
    ...adapter,
    is_active: isActive,
    has_api_key: adapter.has_api_key || !!apiKey,
  });

  // Esc key closes the sheet.
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSave(e) {
    if (e?.preventDefault) e.preventDefault();
    setSaving(true);
    setError(null);
    const patch = {
      is_active: isActive,
      default_model: defaultModel || null,
      available_models: availableModels,
    };
    if (!isBuiltin) {
      patch.display_name = displayName;
      patch.description = description;
      patch.base_url = baseUrl;
    }
    if (apiKey) patch.api_key = apiKey;
    const res = await onSave(patch);
    setSaving(false);
    if (!res.ok) setError(res.error || 'Falha ao salvar');
    else setApiKey('');
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    // v10.6 fix: o teste roda contra a key PERSISTIDA no DB. Se o user
    // digitou uma key nova (ou mudou base_url/modelos) e não salvou, o
    // teste rodaria contra estado antigo → HTTP 401. Persiste antes.
    const patch = {
      is_active: isActive,
      default_model: defaultModel || null,
      available_models: availableModels,
    };
    if (!isBuiltin) {
      patch.display_name = displayName;
      patch.description = description;
      patch.base_url = baseUrl;
    }
    if (apiKey) patch.api_key = apiKey;
    const saveRes = await onSave(patch);
    if (!saveRes.ok) {
      setTesting(false);
      setTestResult({ ok: false, error: saveRes.error || 'Falha ao salvar antes do teste' });
      return;
    }
    if (apiKey) setApiKey(''); // limpa o campo após persistir (mask assume)
    const res = await onTest();
    setTesting(false);
    // P4 — carrega o estado RICO do probe (connState/connLabel) além do corpo cru,
    // pra a falha dizer "sem acesso da conta (403)" em vez de um "HTTP 403" opaco.
    setTestResult({ ...(res.data || { ok: false, error: res.error || 'Falha' }), connState: res.connState, connLabel: res.connLabel });
  }

  async function handleDelete() {
    setSaving(true);
    const res = await onDelete();
    setSaving(false);
    if (!res.ok) setError(res.error || 'Falha ao deletar');
  }

  function applyPreset() {
    if (!preset) return;
    setAvailableModels(preset.available_models);
    if (preset.default_model && !defaultModel) setDefaultModel(preset.default_model);
  }

  // "Sugerir modelos": busca os modelos VIVOS do provider (GET /models no Worker,
  // com UA + auth) e ADICIONA os novos automaticamente. Persiste a key antes (o
  // /models precisa dela). Fallback pro preset estático se o provider negar (403).
  async function suggestModels() {
    setSuggestMsg(null);
    setSuggesting(true);
    try {
      if (onListModels) {
        // Garante que o /models roda contra a key/base atuais (mesmo motivo do Testar).
        if (onSave) await onSave({ api_key: apiKey || undefined, base_url: baseUrl, available_models: availableModels });
        const res = await onListModels();
        const live = res?.data?.ok ? res.data.models : null;
        if (Array.isArray(live) && live.length > 0) {
          let added = 0;
          setAvailableModels((prev) => {
            const set = new Set(prev || []);
            for (const m of live) if (!set.has(m)) { set.add(m); added += 1; }
            return Array.from(set);
          });
          if (preset?.default_model && !defaultModel) setDefaultModel(preset.default_model);
          setSuggestMsg({ ok: true, text: added > 0 ? `+${added} novo(s) modelo(s) do provider` : `Nenhum novo — ${live.length} modelo(s) já listado(s)` });
          return;
        }
        // provider negou /models (ex.: xAI 403) → cai no preset
        setSuggestMsg({ ok: false, text: `Sem lista viva (${res?.data?.error || 'erro'}) — usei o preset` });
      }
      applyPreset();
    } finally {
      setSuggesting(false);
    }
  }

  return (
    <aside
      role="dialog"
      aria-modal="true"
      aria-labelledby="adapter-sheet-title"
      className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-apolo-borderSubtle bg-apolo-surface1 shadow-2xl"
    >
      <header className="flex items-center gap-2 border-b border-apolo-borderSubtle p-4">
        <AdapterLogo adapter={adapter} size={28} />
        <div className="min-w-0 flex-1">
          <h2 id="adapter-sheet-title" className="flex items-center gap-1.5 truncate text-sm font-semibold">
            <ModelStatusDot
              connected={connected}
              size={8}
              title={connected ? 'Conectado' : (isBuiltin ? 'Sem API key' : 'Inativo / sem API key')}
            />
            <span className="truncate">{adapter.display_name}</span>
          </h2>
          <p className="truncate text-xs text-apolo-textTertiary">
            {isBuiltin ? 'Built-in' : 'Custom'} · {adapter.kind}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="rounded p-1 hover:bg-apolo-surface2"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </header>

      <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-3 text-sm">
          <label className="flex items-center justify-between rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 py-2">
            <span>Ativo</span>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4"
            />
          </label>

          {!isBuiltin && (
            <>
              <Field label="Display name" required value={displayName} onChange={setDisplayName} />
              <Field label="Descrição" value={description} onChange={setDescription} />
              <Field label="Base URL" required value={baseUrl} onChange={setBaseUrl} />
            </>
          )}

          {isBuiltin && <ReadOnlyField label="Base URL" value={adapter.base_url} />}

          <label className="flex flex-col gap-1">
            <span className="text-apolo-textSecondary">API Key</span>
            {adapter.api_key_mask && !apiKey && (
              <span className="text-xs text-apolo-textTertiary">
                Atual: <code>{adapter.api_key_mask}</code> (escreva pra substituir)
              </span>
            )}
            <div className="flex items-center gap-1 rounded-md border border-apolo-border bg-apolo-surface2 px-2 py-1.5">
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={adapter.has_api_key ? '••••••••' : 'sk-...'}
                aria-label="API Key"
                className="flex-1 bg-transparent text-sm placeholder:text-apolo-textTertiary focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                aria-label={showKey ? 'Ocultar' : 'Mostrar'}
                className="p-1 text-apolo-textTertiary hover:text-apolo-textPrimary"
              >
                {showKey ? <EyeOff size={14} aria-hidden="true" /> : <Eye size={14} aria-hidden="true" />}
              </button>
            </div>
          </label>

          <Field
            label="Modelo padrão"
            value={defaultModel}
            onChange={setDefaultModel}
            placeholder="gpt-5.6-sol, claude-sonnet-5, gemini-3.6-flash, …"
          />

          <ModelMultiSelect
            adapterName={adapter.name}
            value={availableModels}
            onChange={setAvailableModels}
            defaultModel={defaultModel}
            onSuggest={onListModels || preset ? suggestModels : undefined}
            suggestBusy={suggesting}
          />
          {suggestMsg && (
            <p className={`mt-1 text-[11px] ${suggestMsg.ok ? 'text-emerald-400' : 'text-apolo-textTertiary'}`}>
              {suggestMsg.text}
            </p>
          )}

          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={handleTest}
              disabled={testing}
              className="inline-flex items-center gap-1 rounded-md border border-apolo-border px-3 py-1.5 text-sm hover:bg-apolo-surface2 disabled:opacity-60"
            >
              {testing ? (
                <Loader2 size={14} className="animate-spin" aria-hidden="true" />
              ) : (
                <Zap size={14} aria-hidden="true" />
              )}
              Testar
            </button>
            {testResult && (
              <span
                role="status"
                className={`inline-flex items-center gap-1 text-xs ${
                  testResult.ok ? 'text-emerald-400' : 'text-apolo-error'
                }`}
              >
                {testResult.ok ? (
                  <CheckCircle2 size={14} aria-hidden="true" />
                ) : (
                  <AlertCircle size={14} aria-hidden="true" />
                )}
                {testResult.ok
                  ? `OK · ${testResult.latency_ms}ms · ${testResult.models_count || 0} models`
                  : testResult.connLabel || testResult.error || 'Falhou'}
              </span>
            )}
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-md border border-apolo-error/40 bg-apolo-error/10 px-2 py-1 text-xs text-apolo-error"
            >
              {error}
            </div>
          )}
        </div>
      </form>

      <footer className="flex items-center gap-2 border-t border-apolo-borderSubtle p-3">
        {!isBuiltin &&
          (confirmDelete ? (
            <>
              <button
                type="button"
                onClick={handleDelete}
                disabled={saving}
                className="inline-flex items-center gap-1 rounded-md bg-apolo-error px-3 py-1.5 text-sm text-white disabled:opacity-60"
              >
                <Trash2 size={14} aria-hidden="true" /> Confirmar
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="text-xs text-apolo-textTertiary hover:text-apolo-textSecondary"
              >
                Cancelar
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-1 rounded-md border border-apolo-error/40 px-3 py-1.5 text-sm text-apolo-error hover:bg-apolo-error/10"
            >
              <Trash2 size={14} aria-hidden="true" /> Deletar
            </button>
          ))}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="ml-auto inline-flex items-center gap-1 rounded-md bg-apolo-accent px-3 py-1.5 text-sm font-medium text-apolo-bg disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Save size={14} aria-hidden="true" />}
          Salvar
        </button>
      </footer>
    </aside>
  );
}

function Field({ label, required, value, onChange, placeholder }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-apolo-textSecondary">
        {label}
        {required && <span className="ml-1 text-apolo-error">*</span>}
      </span>
      <input
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-md border border-apolo-border bg-apolo-surface2 px-2 py-1.5 placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none"
      />
    </label>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-apolo-textSecondary">{label}</span>
      <input
        readOnly
        value={value}
        className="rounded-md border border-apolo-borderSubtle bg-apolo-surface3 px-2 py-1.5 text-apolo-textTertiary"
      />
    </label>
  );
}
